import { Checkbox, Divider, Group, Stack } from '@mantine/core';
import { hideNotification, showNotification } from '@mantine/notifications';
import type {
	LiteWorkspace,
	WorkspaceFlags,
	WorkspaceFlagsInPatch,
} from '@repo/api-codegen';
import {
	useGetWorkspaceFlags,
	useUpdateWorkspaceFlags,
} from '@repo/api-codegen';
import { Button, Text, TextInput, Title } from '@repo/foundations';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import FullWidthLoadingSpinner from '../../../components/LoadingSpinner';
import { SuperuserWorkspaceSelector } from '../components/SuperuserWorkspaceSelector';

const useChangesWithNotification = () => {
	const [changes, setChanges] = useState<Record<string, boolean>>({});

	const showUnsavedChangesNotification = useCallback(
		(unsavedChanges: Record<string, boolean>) => {
			const notificationId = 'unsaved-changes';

			hideNotification(notificationId);

			if (Object.keys(unsavedChanges).length === 0) {
				return;
			}

			showNotification({
				id: notificationId,
				title: 'Unsaved Changes',
				color: 'blue',
				autoClose: false,
				message: (
					<Stack spacing="xs">
						<Text size="xs" inherit>
							You have unsaved changes to:
						</Text>
						{Object.keys(unsavedChanges).map((change) => (
							<Text key={change} size="xs" inherit>
								- {change}
							</Text>
						))}
						<Text size="xs" inherit>
							If you are sure about your changes, provide a TOTP and press Save
							Changes.
						</Text>
					</Stack>
				),
			});
		},
		[]
	);

	const setChangesWithNotification = useCallback(
		(newChanges: Record<string, boolean>) => {
			setChanges(newChanges);
			showUnsavedChangesNotification(newChanges);
		},
		[showUnsavedChangesNotification]
	);

	const hasChanges = useMemo(() => Object.keys(changes).length > 0, [changes]);

	return {
		changes,
		setChangesWithNotification,
		hasChanges,
	};
};

function WorkspaceFlagsPage() {
	const [workspace, setWorkspace] = useState<LiteWorkspace | undefined>(
		undefined
	);
	const [totp, setTotp] = useState<string>('');
	const { changes, setChangesWithNotification, hasChanges } =
		useChangesWithNotification();

	const queryClient = useQueryClient();

	const {
		data: fetchedFlags,
		isLoading: isLoadingFlags,
		refetch: refetchFlags,
	} = useGetWorkspaceFlags<WorkspaceFlags>(
		{
			pathParams: {
				workspaceId: workspace?.id ?? '',
			},
		},
		{
			enabled: !!workspace?.id,
		}
	);

	// Derive the displayed flags by combining fetched flags with changed flags
	const displayedFlags = useMemo(() => {
		if (!fetchedFlags) {
			return {};
		}
		return { ...fetchedFlags, ...changes } as Record<string, boolean>;
	}, [fetchedFlags, changes]);

	const { mutate: updateWorkspaceFlags, isPending } = useUpdateWorkspaceFlags({
		onSuccess: () => {
			// Optimistically update fetchedFlags to displayedFlags
			queryClient.setQueryData(
				['admin', 'workspace_flags', workspace?.id],
				displayedFlags
			);
			// Clear changes after successful update
			setChangesWithNotification({});
			setTotp('');
			showNotification({
				title: 'Success',
				message: 'Workspace flags updated successfully',
				color: 'green',
				autoClose: 3000,
			});
			// Refetch to ensure we have the latest flags from the server
			refetchFlags();
		},
		onError: (error) => {
			showNotification({
				title: 'Error',
				message: `Failed to update workspace flags: ${error?.detail || 'Unknown error'}`,
				color: 'red',
				autoClose: 5000,
			});
		},
	});

	const handleWorkspaceChange = useCallback(
		(newWorkspace?: LiteWorkspace) => {
			setWorkspace(newWorkspace);
			setChangesWithNotification({});
			setTotp('');
		},
		[setChangesWithNotification]
	);

	const handleFlagChange = useCallback(
		(flagName: string, value: boolean) => {
			if (!fetchedFlags || !(flagName in fetchedFlags)) {
				return;
			}

			const newChanges = { ...changes };

			// If the value matches the fetched value, remove it from changes
			if (fetchedFlags[flagName as keyof WorkspaceFlags] === value) {
				delete newChanges[flagName];
			} else {
				// Otherwise, add it to changes
				newChanges[flagName] = value;
			}

			setChangesWithNotification(newChanges);
		},
		[fetchedFlags, changes, setChangesWithNotification]
	);

	const handleSave = useCallback(() => {
		if (!totp.trim()) {
			showNotification({
				title: 'Error',
				message: 'TOTP code is required to save workspace flags',
				color: 'red',
				autoClose: 5000,
			});
			return;
		}

		if (workspace?.id) {
			updateWorkspaceFlags({
				pathParams: { workspaceId: workspace.id },
				body: {
					...changes,
					totp: totp.trim(),
				} as WorkspaceFlagsInPatch,
			});
		}
	}, [workspace?.id, changes, updateWorkspaceFlags, totp]);

	if (workspace && isLoadingFlags) {
		return <FullWidthLoadingSpinner />;
	}

	return (
		<Stack p="md">
			<Stack w={500}>
				<Group>
					<Title order={5}>Workspace</Title>
					<SuperuserWorkspaceSelector
						selected={workspace}
						setSelected={handleWorkspaceChange}
					/>
					{workspace && <Stack>Workspace ID: {workspace.id}</Stack>}
				</Group>
			</Stack>

			{workspace && !isLoadingFlags && fetchedFlags && (
				<>
					<Divider color="dark" />
					<Stack>
						<Title order={5}>Workspace Flags</Title>
						<Text size="md" inherit>
							View and edit workspace flags. Changes must be saved to take
							effect.
						</Text>
						{Object.entries(displayedFlags).map(([flagName, value]) => (
							<Checkbox
								key={flagName}
								label={flagName}
								checked={value}
								onChange={(event) =>
									handleFlagChange(flagName, event.currentTarget.checked)
								}
							/>
						))}
						<Group mt="md">
							<TextInput
								label="One Time Superuser Token"
								placeholder="Enter TOTP code"
								value={totp}
								onChange={(event) => setTotp(event.currentTarget.value)}
								style={{ width: '40%' }}
							/>
							<Button
								size="md"
								variant="primary"
								onClick={handleSave}
								loading={isPending}
								disabled={!hasChanges || isPending || !totp.trim()}
							>
								{isPending ? 'Saving...' : 'Save Changes'}
							</Button>
						</Group>
					</Stack>
				</>
			)}
		</Stack>
	);
}

export default WorkspaceFlagsPage;
